/**
 * Store implementation for Bandmate (SPEC §4.2, §10) — `createStore()`
 * realises the frozen `Store` contract from `./store-api`.
 *
 * - **Undo** is a linear stack of deep-cloned session snapshots (cap 50) taken
 *   *before* each mutation, tagged with who caused it and a label. `select`
 *   never pushes; every other mutation does, including `replaceSession`.
 * - **Authorship**: every note/hit a mutation writes gets `by` from the
 *   `ApplyMeta` unless the input already carries an explicit `by`.
 * - **Transient layer**: audition previews are set with `setTransient()`;
 *   `current()` returns the transient session when set (the audio engine
 *   reads it every 16th), `get()` always returns the committed one. The
 *   transient never enters the undo stack and autosave ignores it.
 * - **Pure core**: `applyMutation()` is a pure function of (session, mutation,
 *   author) that returns a fresh session plus counts; `apply()` commits its
 *   result and `dryRun()` discards it, so both always agree.
 *
 * Every stored note is normalised: integer `step` wrapped into 0..63, integer
 * `length` clamped 1..64 (one loop), `velocity` clamped 0.05..1, integer
 * `pitch` clamped to MIDI 0..127. Undo labels and human-action details are
 * clipped to `TEXT_CAP` characters so `get_session` stays within budget.
 */
import {
  BARS,
  DEFAULT_TRACK_VOLUME_DB,
  MELODIC_TRACK_IDS,
  SWING_MAX,
  SWING_MIN,
  TEMPO_MAX,
  TEMPO_MIN,
  TONICS,
  TOTAL_STEPS,
  TRACK_IDS,
  TRACK_RANGE,
  VOLUME_MAX_DB,
  VOLUME_MIN_DB,
  clamp,
  cloneSession,
  splitStep,
  type AppMode,
  type Author,
  type DrumHit,
  type HumanAction,
  type MelodicTrackId,
  type Note,
  type PendingAudition,
  type Session,
  type Tonic,
  type TrackId,
} from '../types';
import type {
  ApplyMeta,
  ApplyResult,
  ChangeMeta,
  DrumHitInput,
  Mutation,
  NoteInput,
  Store,
  UndoEntry,
  UndoResult,
} from './store-api';

/** Maximum number of undo snapshots kept (SPEC §4.2). */
export const UNDO_CAP = 50;
/** Number of human actions remembered for `get_session.human.recent`. */
export const HUMAN_LOG_CAP = 8;
/** Velocity floor applied to every stored note/hit. */
export const VELOCITY_MIN = 0.05;
/** Velocity used when an input velocity is not a finite number. */
export const VELOCITY_DEFAULT = 0.8;
/** Pitch used when an input pitch is not a finite number (C4). */
export const PITCH_DEFAULT = 60;
/** Longest note the store keeps: one full loop, so a note never overlaps itself. */
export const LENGTH_MAX = TOTAL_STEPS;
/** Maximum length of an undo label or a human-action detail (types.ts says <= 80). */
export const TEXT_CAP = 80;

/** Clip a label/detail to `TEXT_CAP` characters (types.ts: "<= 80 chars"). */
export function clipText(text: string): string {
  return text.length > TEXT_CAP ? text.slice(0, TEXT_CAP) : text;
}

/** A fresh, empty session: A minor, 82 bpm, swing 30, lofi, default volumes. */
export function blankSession(): Session {
  const flags = (id: TrackId) => ({ muted: false, solo: false, volume: DEFAULT_TRACK_VOLUME_DB[id] });
  return {
    version: 1,
    key: { tonic: 'A', mode: 'minor' },
    tempo: 82,
    swing: 30,
    style: 'lofi',
    metronome: false,
    tracks: {
      drums: { id: 'drums', hits: [], ...flags('drums') },
      bass: { id: 'bass', notes: [], ...flags('bass') },
      chords: { id: 'chords', notes: [], ...flags('chords') },
      lead: { id: 'lead', notes: [], ...flags('lead') },
    },
    progression: null,
    progressionRhythm: null,
    bassPattern: null,
    ui: { selectedTrack: 'lead', selectedBar: 0 },
  };
}

/** Wrap any number onto an integer step 0..63. */
function wrapStep(step: number): number {
  const n = Number.isFinite(step) ? Math.round(step) : 0;
  return ((n % TOTAL_STEPS) + TOTAL_STEPS) % TOTAL_STEPS;
}

function normVelocity(v: number): number {
  return Number.isFinite(v) ? clamp(v, VELOCITY_MIN, 1) : VELOCITY_DEFAULT;
}

/** Normalise a note input into a stored `Note`, filling `by` from the author. */
export function normalizeNote(n: NoteInput, by: Author): Note {
  return {
    step: wrapStep(n.step),
    pitch: Number.isFinite(n.pitch) ? clamp(Math.round(n.pitch), 0, 127) : PITCH_DEFAULT,
    length: Number.isFinite(n.length) ? clamp(Math.round(n.length), 1, LENGTH_MAX) : 1,
    velocity: normVelocity(n.velocity),
    by: n.by ?? by,
  };
}

/** Normalise a drum-hit input into a stored `DrumHit`, filling `by` from the author. */
export function normalizeHit(h: DrumHitInput, by: Author): DrumHit {
  return {
    step: wrapStep(h.step),
    lane: h.lane,
    velocity: normVelocity(h.velocity),
    by: h.by ?? by,
  };
}

/**
 * Signed semitone distance from one tonic to another, normalised to -5..+6
 * (the shortest direction, ties going up). Used by `setKey{transposeContent}`.
 */
export function semitoneDelta(from: Tonic, to: Tonic): number {
  const d = (((TONICS.indexOf(to) - TONICS.indexOf(from)) % 12) + 12) % 12;
  return d > 6 ? d - 12 : d;
}

/** Move a pitch by `delta` semitones, then fold it back into `[low, high]` by octaves. */
export function transposeInto(pitch: number, delta: number, range: readonly [number, number]): number {
  let p = pitch + delta;
  const [low, high] = range;
  while (p < low) p += 12;
  while (p > high) p -= 12;
  return p;
}

const CHORD_NAME_RE = /^([A-G])(#|b)?(.*)$/;

/**
 * Transpose the root of a chord name (`Fmaj7` +3 → `G#maj7`), spelling the new
 * root with sharps. Names that do not start with a root letter are returned as is.
 */
export function transposeChordName(name: string, delta: number): string {
  const m = CHORD_NAME_RE.exec(name);
  if (!m) return name;
  const base: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  let pc = base[m[1]!]!;
  if (m[2] === '#') pc += 1;
  else if (m[2] === 'b') pc -= 1;
  const root = TONICS[(((pc + delta) % 12) + 12) % 12]!;
  return root + (m[3] ?? '');
}

interface Counts {
  removedHuman: number;
  removedAgent: number;
  written: number;
}

/** Everything a pure mutation application yields. */
export interface MutationOutcome extends Counts {
  next: Session;
  transposed?: Partial<Record<MelodicTrackId, number>>;
  /** Tracks whose content or flags changed. */
  tracks: TrackId[];
  /** Steps written on a single track (for the agent-reveal animation). */
  steps?: { track: TrackId; steps: number[] };
}

function countRemoved(items: ReadonlyArray<{ by: Author }>, counts: Counts): void {
  for (const it of items) {
    if (it.by === 'human') counts.removedHuman += 1;
    else counts.removedAgent += 1;
  }
}

/** Keep the last occurrence for each key, preserving the order of first appearance. */
function dedupeLastWins<T>(items: T[], keyOf: (t: T) => string): T[] {
  const map = new Map<string, T>();
  for (const it of items) {
    if (map.has(keyOf(it))) map.delete(keyOf(it));
    map.set(keyOf(it), it);
  }
  return [...map.values()];
}

const noteKey = (n: { step: number; pitch: number }) => `${n.step}:${n.pitch}`;
const hitKey = (h: { step: number; lane: string }) => `${h.step}:${h.lane}`;

function uniqueSortedSteps(items: ReadonlyArray<{ step: number }>): number[] {
  return [...new Set(items.map((i) => i.step))].sort((a, b) => a - b);
}

/**
 * Pure core of the store: apply `m` to a deep clone of `s` as author `by`.
 * Never touches `s`. `apply()` commits the result, `dryRun()` discards it.
 */
export function applyMutation(s: Session, m: Mutation, by: Author): MutationOutcome {
  const next = cloneSession(s);
  const counts: Counts = { removedHuman: 0, removedAgent: 0, written: 0 };
  const out: MutationOutcome = { next, ...counts, tracks: [] };
  const tally = () => {
    out.removedHuman = counts.removedHuman;
    out.removedAgent = counts.removedAgent;
    out.written = counts.written;
    return out;
  };

  switch (m.type) {
    case 'select': {
      if (m.track !== undefined) next.ui.selectedTrack = m.track;
      if (m.bar !== undefined && Number.isFinite(m.bar)) next.ui.selectedBar = clamp(Math.round(m.bar), 0, BARS - 1);
      return tally();
    }

    case 'setGroove': {
      if (m.tempo !== undefined && Number.isFinite(m.tempo)) next.tempo = clamp(m.tempo, TEMPO_MIN, TEMPO_MAX);
      if (m.swing !== undefined && Number.isFinite(m.swing)) next.swing = clamp(m.swing, SWING_MIN, SWING_MAX);
      if (m.style !== undefined) next.style = m.style;
      if (m.metronome !== undefined) next.metronome = m.metronome;
      return tally();
    }

    case 'setKey': {
      const delta = m.transposeContent ? semitoneDelta(next.key.tonic, m.tonic) : 0;
      next.key = { tonic: m.tonic, mode: m.mode };
      if (delta !== 0) {
        const transposed: Partial<Record<MelodicTrackId, number>> = {};
        for (const id of MELODIC_TRACK_IDS) {
          const track = next.tracks[id];
          for (const n of track.notes) n.pitch = transposeInto(n.pitch, delta, TRACK_RANGE[id]);
          transposed[id] = track.notes.length;
          if (track.notes.length > 0) out.tracks.push(id);
        }
        if (next.progression) {
          for (const slot of next.progression) {
            slot.pitches = slot.pitches.map((p) => transposeInto(p, delta, TRACK_RANGE.chords));
            slot.name = transposeChordName(slot.name, delta);
          }
        }
        out.transposed = transposed;
      }
      return tally();
    }

    case 'writeNotes': {
      const track = next.tracks[m.track];
      const incoming = dedupeLastWins(
        m.notes.map((n) => normalizeNote(n, by)),
        noteKey,
      );
      if (m.mode === 'replace') {
        countRemoved(track.notes, counts);
        track.notes = incoming;
      } else {
        const replacedKeys = new Set(incoming.map(noteKey));
        const kept: Note[] = [];
        for (const n of track.notes) {
          if (replacedKeys.has(noteKey(n))) countRemoved([n], counts);
          else kept.push(n);
        }
        track.notes = [...kept, ...incoming];
      }
      counts.written = incoming.length;
      if (m.track === 'chords') {
        next.progression = null;
        next.progressionRhythm = null;
      } else if (m.track === 'bass') {
        next.bassPattern = null;
      }
      out.tracks.push(m.track);
      out.steps = { track: m.track, steps: uniqueSortedSteps(incoming) };
      return tally();
    }

    case 'setDrumHits': {
      const track = next.tracks.drums;
      const incoming = dedupeLastWins(
        m.hits.map((h) => normalizeHit(h, by)),
        hitKey,
      );
      if (m.mode === 'replace') {
        const bars = m.bars ? new Set(m.bars) : null;
        const lanes = m.lanes ? new Set(m.lanes) : null;
        // Hits outside the bars/lanes filter survive — unless an incoming hit
        // lands on the same step+lane, which always overwrites (one hit per cell).
        const replacedKeys = new Set(incoming.map(hitKey));
        const kept: DrumHit[] = [];
        for (const h of track.hits) {
          const inBars = bars === null || bars.has(splitStep(h.step).bar);
          const inLanes = lanes === null || lanes.has(h.lane);
          if ((inBars && inLanes) || replacedKeys.has(hitKey(h))) countRemoved([h], counts);
          else kept.push(h);
        }
        track.hits = [...kept, ...incoming];
      } else {
        const replacedKeys = new Set(incoming.map(hitKey));
        const kept: DrumHit[] = [];
        for (const h of track.hits) {
          if (replacedKeys.has(hitKey(h))) countRemoved([h], counts);
          else kept.push(h);
        }
        track.hits = [...kept, ...incoming];
      }
      counts.written = incoming.length;
      out.tracks.push('drums');
      out.steps = { track: 'drums', steps: uniqueSortedSteps(incoming) };
      return tally();
    }

    case 'setTrack': {
      const track = next.tracks[m.track];
      if (m.muted !== undefined) track.muted = m.muted;
      if (m.solo !== undefined) track.solo = m.solo;
      if (m.volume !== undefined && Number.isFinite(m.volume)) track.volume = clamp(m.volume, VOLUME_MIN_DB, VOLUME_MAX_DB);
      out.tracks.push(m.track);
      return tally();
    }

    case 'clearTrack': {
      if (m.track === 'drums') {
        countRemoved(next.tracks.drums.hits, counts);
        next.tracks.drums.hits = [];
      } else {
        countRemoved(next.tracks[m.track].notes, counts);
        next.tracks[m.track].notes = [];
        if (m.track === 'chords') {
          next.progression = null;
          next.progressionRhythm = null;
        } else if (m.track === 'bass') {
          next.bassPattern = null;
        }
      }
      out.tracks.push(m.track);
      return tally();
    }

    case 'setProgression': {
      const track = next.tracks.chords;
      countRemoved(track.notes, counts);
      const incoming = dedupeLastWins(
        m.notes.map((n) => normalizeNote(n, by)),
        noteKey,
      );
      track.notes = incoming;
      counts.written = incoming.length;
      next.progression = JSON.parse(JSON.stringify(m.slots)) as Session['progression'];
      next.progressionRhythm = m.rhythm;
      out.tracks.push('chords');
      out.steps = { track: 'chords', steps: uniqueSortedSteps(incoming) };
      return tally();
    }

    case 'setBassPattern': {
      const track = next.tracks.bass;
      countRemoved(track.notes, counts);
      const incoming = dedupeLastWins(
        m.notes.map((n) => normalizeNote(n, by)),
        noteKey,
      );
      track.notes = incoming;
      counts.written = incoming.length;
      next.bassPattern = m.pattern;
      out.tracks.push('bass');
      out.steps = { track: 'bass', steps: uniqueSortedSteps(incoming) };
      return tally();
    }

    case 'replaceSession': {
      for (const id of TRACK_IDS) countRemoved(contentOf(s, id), counts);
      const replacement = cloneSession(m.session);
      for (const id of MELODIC_TRACK_IDS) {
        replacement.tracks[id].notes = replacement.tracks[id].notes.map((n) => normalizeNote(n, by));
      }
      replacement.tracks.drums.hits = replacement.tracks.drums.hits.map((h) => normalizeHit(h, by));
      for (const id of TRACK_IDS) counts.written += contentOf(replacement, id).length;
      out.next = replacement;
      out.tracks.push(...TRACK_IDS);
      return tally();
    }
  }
}

function contentOf(s: Session, id: TrackId): ReadonlyArray<{ by: Author }> {
  return id === 'drums' ? s.tracks.drums.hits : s.tracks[id].notes;
}

/** Tracks whose serialised content differs between two sessions. */
function changedTracks(a: Session, b: Session): TrackId[] {
  return TRACK_IDS.filter((id) => JSON.stringify(a.tracks[id]) !== JSON.stringify(b.tracks[id]));
}

interface Snapshot {
  entry: UndoEntry;
  /** The committed session as it was BEFORE the mutation tagged by `entry`. */
  before: Session;
}

type Subscriber = (s: Session, meta: ChangeMeta) => void;

/**
 * Create a `Store` seeded with a deep copy of `initial`. See the module header
 * for the undo / transient / authorship rules.
 *
 * Subscribers receive the **committed** session for `mutation`, `undo` and
 * `load` changes, and `current()` (the audition preview) for `transient` and
 * `mode` changes. `get()` / `current()` return live objects for the engine's
 * per-16th reads; treat them as read-only.
 */
export function createStore(initial: Session): Store {
  let committed = cloneSession(initial);
  let transient: Session | null = null;
  /** Oldest first; `undoStack()` reverses it. */
  const stack: Snapshot[] = [];
  let nextId = 1;
  const subscribers = new Set<Subscriber>();
  const humanLog: HumanAction[] = [];
  let recording: { notes: number; at: number } | null = null;
  let mode: AppMode = 'idle';
  let audition: PendingAudition | null = null;

  const current = (): Session => transient ?? committed;

  function notify(s: Session, meta: ChangeMeta): void {
    for (const fn of [...subscribers]) {
      try {
        fn(s, meta);
      } catch (err) {
        console.error('[bandmate] store subscriber threw', err);
      }
    }
  }

  function pop(count: number): UndoResult {
    if (count <= 0 || stack.length === 0) return { applied: false, undone: [], reason: 'empty' };
    const popped = stack.splice(stack.length - Math.min(count, stack.length));
    const oldest = popped[0]!;
    const previous = committed;
    committed = oldest.before;
    const undone = popped.map((p) => p.entry).reverse();
    const top = undone[0]!;
    notify(committed, { source: 'undo', by: top.by, label: top.label, tracks: changedTracks(previous, committed) });
    return { applied: true, undone };
  }

  return {
    get: () => committed,
    current,
    hasTransient: () => transient !== null,

    subscribe(fn) {
      subscribers.add(fn);
      return () => {
        subscribers.delete(fn);
      };
    },

    apply(m, meta: ApplyMeta): ApplyResult {
      const outcome = applyMutation(committed, m, meta.by);
      let id: string | null = null;
      if (m.type !== 'select') {
        id = `u${nextId++}`;
        // Deep-clone the snapshot: `get()` hands out the live object, and a
        // careless consumer mutating it must not rewrite history.
        stack.push({
          entry: { id, by: meta.by, label: clipText(meta.label), at: Date.now() },
          before: cloneSession(committed),
        });
        if (stack.length > UNDO_CAP) stack.splice(0, stack.length - UNDO_CAP);
      }
      committed = outcome.next;
      const change: ChangeMeta = { source: 'mutation', by: meta.by, label: clipText(meta.label), tracks: outcome.tracks };
      if (meta.by === 'agent' && outcome.steps && outcome.steps.steps.length > 0) change.agentSteps = outcome.steps;
      notify(committed, change);
      const result: ApplyResult = {
        id,
        removedHuman: outcome.removedHuman,
        removedAgent: outcome.removedAgent,
        written: outcome.written,
      };
      if (outcome.transposed) result.transposed = outcome.transposed;
      return result;
    },

    dryRun(m) {
      const { removedHuman, removedAgent, written } = applyMutation(committed, m, 'agent');
      return { removedHuman, removedAgent, written };
    },

    setTransient(s) {
      transient = s ? cloneSession(s) : null;
      notify(current(), { source: 'transient' });
    },

    undo(n = 1) {
      return pop(Number.isFinite(n) ? Math.floor(n) : 1);
    },

    undoTo(id) {
      const index = stack.findIndex((snap) => snap.entry.id === id);
      if (index < 0) return { applied: false, undone: [], reason: 'unknown_id' };
      return pop(stack.length - index);
    },

    undoStack() {
      return stack.map((snap) => ({ ...snap.entry })).reverse();
    },

    logHuman(a) {
      humanLog.push({ at: a.at ?? Date.now(), kind: a.kind, detail: clipText(a.detail) });
      if (humanLog.length > HUMAN_LOG_CAP) humanLog.splice(0, humanLog.length - HUMAN_LOG_CAP);
    },
    humanRecent() {
      return humanLog.map((a) => ({ ...a })).reverse();
    },

    lastRecording: () => (recording ? { ...recording } : null),
    setLastRecording(r) {
      recording = r ? { ...r } : null;
    },

    mode: () => mode,
    setMode(m) {
      mode = m;
      notify(current(), { source: 'mode' });
    },

    audition: () => (audition ? { ...audition, labels: [...audition.labels] } : null),
    setAudition(a) {
      audition = a ? { ...a, labels: [...a.labels] } : null;
    },

    load(s) {
      committed = cloneSession(s);
      transient = null;
      stack.length = 0;
      notify(committed, { source: 'load', tracks: [...TRACK_IDS] });
    },
  };
}
